import { useEffect, useState } from 'react';
import { DataProvider, useDataProvider } from 'react-admin';
import { CsvImportConfig, ImportLookupSource, ImportLookups, ImportOption } from './csv-import-types';

/** Enough for every list these importers reference; the edit forms load 500 at most. */
const LOOKUP_PAGE_SIZE: number = 1000;

export interface ImportLookupsState {
    lookups: ImportLookups;
    loading: boolean;
    failed: boolean;
}

/**
 * Load the choice lists a config's reference columns pick from, once per dialog. A failed load
 * leaves the lists empty: ids can still be typed, and HQ-API checks them on Validate.
 */
export function useImportLookups(config: CsvImportConfig, enabled: boolean): ImportLookupsState {
    const dataProvider: DataProvider = useDataProvider();
    const [state, setState] = useState<ImportLookupsState>({ lookups: {}, loading: true, failed: false });

    useEffect((): (() => void) => {
        let cancelled: boolean = false;
        if (!enabled) {
            return (): void => { cancelled = true; };
        }
        setState({ lookups: {}, loading: true, failed: false });

        Promise.all(config.lookups.map(async (source: ImportLookupSource): Promise<[string, ImportOption[]]> => {
            const { data } = await dataProvider.getList(source.resource, {
                pagination: { page: 1, perPage: LOOKUP_PAGE_SIZE },
                sort: { field: 'id', order: 'ASC' },
                filter: source.filter ?? {},
            });
            const options: ImportOption[] = data.map((record): ImportOption => ({
                value: String(record.id),
                label: source.label(record),
                name: typeof record.name === 'string' ? record.name : undefined,
            }));

            return [source.key, options];
        }))
            .then((entries: [string, ImportOption[]][]): void => {
                if (!cancelled) {
                    setState({ lookups: Object.fromEntries(entries), loading: false, failed: false });
                }
            })
            .catch((): void => {
                if (!cancelled) {
                    setState({ lookups: {}, loading: false, failed: true });
                }
            });

        return (): void => { cancelled = true; };
    }, [config, dataProvider, enabled]);

    return state;
}
